import {
  AppShellContent,
  Button,
  Group,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
  Title,
  TooltipIcon,
  Upload,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  procedureDraftFromBuilder,
  storeAiProcedureDraft,
} from "../components/procedures/aiProcedureDraft";
import { useWorkScope } from "../components/WorkScopeProvider";
import { getWorkScopeSearch } from "../lib/workSearch";
import {
  buildProcedureCandidate,
  createProcedureFromForm,
  previewProcedureBuilder,
  useFacilityAssetsQuery,
} from "../queries/useProceduresQuery";

const TYPES = [
  { label: "Safety", value: "Safety" },
  { label: "Maintenance", value: "Maintenance" },
  { label: "Operations", value: "Operations" },
  { label: "Reliability", value: "Reliability" },
];
const FREQUENCIES = [
  "Daily",
  "Weekly",
  "Monthly",
  "Quarterly",
  "Yearly",
  "Other",
].map((value) => ({ label: value, value }));

export function isAiBuilderReady(input: {
  title: string;
  type: string | null;
  frequency: string | null;
  hasCustomer: boolean;
  hasFacility: boolean;
}) {
  return Boolean(
    input.title.trim() &&
      input.type &&
      input.frequency &&
      input.hasCustomer &&
      input.hasFacility,
  );
}

export function ProcedureAiBuilderScreen() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const navigate = useNavigate();
  const assetsQuery = useFacilityAssetsQuery();
  const [title, setTitle] = useState("");
  const [assetId, setAssetId] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canBuild = isAiBuilderReady({
    title,
    type,
    frequency,
    hasCustomer: Boolean(selectedCustomer),
    hasFacility: Boolean(selectedFacility),
  });
  const assetOptions =
    assetsQuery.data?.map((asset) => ({
      label: asset.name,
      value: String(asset.id),
    })) ?? [];
  function backToLibrary() {
    if (!selectedCustomer || !selectedFacility) return;
    void navigate({
      to: "/procedures",
      search: getWorkScopeSearch(selectedCustomer, selectedFacility),
    });
  }

  function createManually() {
    if (!selectedCustomer || !selectedFacility) return;
    void navigate({
      to: "/procedures/authoring",
      search: {
        ...getWorkScopeSearch(selectedCustomer, selectedFacility),
        procedureId: undefined,
        aiDraftId: undefined,
      },
    });
  }

  async function build() {
    if (!selectedCustomer || !selectedFacility || !canBuild) return;
    setBusy(true);
    setError(null);
    try {
      const builder = file
        ? await previewProcedureBuilder(
            selectedCustomer.id,
            selectedFacility.id,
            file,
          )
        : await createProcedureFromForm(
            selectedCustomer.id,
            selectedFacility.id,
            {
              title,
              assetId: assetId ?? "1",
              type: type ?? "Maintenance",
              frequency: frequency ?? "Other",
              description,
            },
          );
      if (file && !builder.candidates[0]?.id) {
        throw new Error("The source did not contain a procedure to draft.");
      }
      const generated = file
        ? await buildProcedureCandidate(
            selectedCustomer.id,
            selectedFacility.id,
            builder.id,
            builder.candidates[0]?.id ?? "",
          )
        : builder;
      const draft = procedureDraftFromBuilder(generated, {
        category: type ?? "Maintenance",
        title,
        description,
      });
      const draftId = storeAiProcedureDraft({
        draft,
        sourceMaterial: {
          fileName: file?.name ?? null,
          retained: false,
          documentId: null,
          documentNumber: null,
        },
      });
      void navigate({
        to: "/procedures/authoring",
        search: {
          ...getWorkScopeSearch(selectedCustomer, selectedFacility),
          procedureId: undefined,
          aiDraftId: draftId,
        },
      });
    } catch (buildError) {
      setError(
        buildError instanceof Error
          ? buildError.message
          : "The AI draft could not be generated. Try again or continue manually.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShellContent>
      <Stack gap={24}>
        <Button onClick={backToLibrary} size="sm" variant="ghost">
          ← Back to Procedures
        </Button>
        <div className="text-center">
          <Stack gap={8}>
            <Title as="h1" size="xl">
              Build a Procedure with{" "}
              <span className="text-primary">AW Assistant</span>
            </Title>
            <Text tone="subtle">
              Add details below, and we'll draft a complete procedure for you to
              review and edit.
            </Text>
          </Stack>
        </div>
        <div className="mx-auto w-full max-w-[960px]">
          <Tile hasBorder padding="comfortable" width="full">
            <Stack gap={16}>
              {error ? (
                <Stack gap={8}>
                  <Text tone="negative">{error}</Text>
                  <Button
                    disabled={busy}
                    onClick={() => void build()}
                    size="sm"
                    variant="outline"
                  >
                    Try again
                  </Button>
                </Stack>
              ) : null}
              <Input
                label="Title"
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Monthly HVAC Inspection"
                required
                value={title}
              />
              <Select
                disabled={assetsQuery.isPending}
                label="Asset"
                searchable
                onChange={(value) => setAssetId(value ?? null)}
                options={assetOptions}
                placeholder={
                  assetsQuery.isPending ? "Loading assets..." : "Search..."
                }
                value={assetId}
              />
              <div className="grid grid-cols-1 gap-(--spacing-12) md:grid-cols-2">
                <Select
                  label="Type"
                  onChange={(value) => setType(value ?? null)}
                  options={TYPES}
                  required
                  value={type}
                />
                <Select
                  label="Frequency"
                  onChange={(value) => setFrequency(value ?? null)}
                  options={FREQUENCIES}
                  required
                  value={frequency}
                />
              </div>
              <Stack gap={4}>
                <Group gap={4}>
                  <label
                    className="font-(number:--font-weight-medium)"
                    htmlFor="procedure-ai-description"
                  >
                    Describe your procedure
                  </label>
                  <TooltipIcon
                    description="Add fields, topics, or considerations to include. More detail leads to a better procedure."
                    label="Procedure description"
                    size="sm"
                  />
                </Group>
                <Textarea
                  id="procedure-ai-description"
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="e.g. Quarterly inspection of rooftop HVAC unit."
                  value={description}
                />
              </Stack>
              <Stack gap={8}>
                <Text weight="medium">
                  Attach manuals and digitized checklists
                </Text>
                <Upload
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  acceptedFormatsLabel="XLSX"
                  buttonLabel={file?.name ?? "Upload..."}
                  className="w-full"
                  maxSizeLabel="10 MB"
                  onFilesSelected={(files) => setFile(files[0] ?? null)}
                  variant="file"
                />
              </Stack>
              <div className="text-center">
                <Button
                  disabled={!canBuild || busy}
                  icon="ai-filled"
                  loading={busy}
                  onClick={() => void build()}
                >
                  Build with AI
                </Button>
              </div>
            </Stack>
          </Tile>
        </div>
        <div className="text-center">
          <Text tone="subtle">Or</Text>
          <div className="mt-(--spacing-12)">
            <Button
              disabled={busy}
              icon="edit-pen"
              onClick={createManually}
              variant="outline"
            >
              Create manually
            </Button>
          </div>
        </div>
      </Stack>
    </AppShellContent>
  );
}
